import { useMemo } from 'react';

import _ from 'lodash';
import moment from 'moment';
import { XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area, ResponsiveContainer } from 'recharts';
import { LoadSkeleton } from 'src/v2/components/ui/loadSkeleton';

import { useDashboardStats } from '~components/hooks/dashboard/useDashboardStats';
import { black, primaryColors } from '~components/theme/styles/colors';
import { label3 } from '~components/theme/typography';
import { EDisplayType } from '~components/types';
import { displayFormatUnits } from '~components/utils/utils';
import { getTimestampAtStartOfDayUTC } from '~utils/date';
import { createTimeTicks } from '~utils/graphs';

import { GridWrapper } from '../gridWrapper';

import { TvlAreaGraphTooltip } from './tvlAreaGraphTooltip';

const TVL_AREA_GRAPH_INTERVAL = 'Year';

export const TvlAreaGraph: React.FC = () => {
  const { data: dashboardData, isLoading: isDashboardLoading } = useDashboardStats(TVL_AREA_GRAPH_INTERVAL);

  const totalTvlData = useMemo(
    () =>
      _.sortBy(dashboardData, ({ timestamp }) => moment(timestamp).valueOf()).map(
        ({
          timestamp,
          stats: {
            tvl: { tvlUsd },
          },
        }) => ({
          timestamp: getTimestampAtStartOfDayUTC(new Date(timestamp).valueOf() / 1000) * 1000,
          tvl: Number(tvlUsd),
        }),
      ),
    [JSON.stringify(dashboardData)],
  );

  if (isDashboardLoading) {
    return <LoadSkeleton isLoading={isDashboardLoading} width={1088} height={400} />;
  }

  return (
    <GridWrapper
      sx={{
        height: '400px',
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          width={1088}
          height={400}
          data={totalTvlData}
          margin={{
            top: 10,
            right: 8,
            left: -22,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id="colorTvl" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(124, 79, 219, 0.8)" />
              <stop offset="50%" stopColor="rgba(0, 251, 234, 0.5)" />
              <stop offset="100%" stopColor="#B5EDFF" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" color={primaryColors[900]} />
          <XAxis
            style={{
              ...label3,
              fill: primaryColors[400],
            }}
            type="number"
            axisLine={false}
            {...createTimeTicks(totalTvlData, TVL_AREA_GRAPH_INTERVAL)}
          />
          <YAxis
            style={{
              ...label3,
              fill: primaryColors[400],
            }}
            axisLine={false}
            tickFormatter={val =>
              displayFormatUnits({ value: parseFloat(val), displayType: EDisplayType.ShortVST, decimal: 0 })
            }
          />
          <Area type="monotone" dataKey="tvl" stroke="url(#colorTvl)" fill={black['45%']} strokeWidth={2} />
          <Tooltip content={<TvlAreaGraphTooltip />} />
        </AreaChart>
      </ResponsiveContainer>
    </GridWrapper>
  );
};
